import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import * as Collapsible from '@radix-ui/react-collapsible'

import { useOnEvent } from '@sketch/utils'
import {
  useModalContext,
  SelectDropdownActionDropdown,
} from '@sketch/components'
import {
  ShareDropPayload,
  DroppableLinkItem,
  RouteParams,
} from '@sketch/modules-common'
import { ConfirmMoveModal } from '../../modals'

import { RenameInlineEditor } from '../InlineEditor'
import ProjectDropdown from '../ProjectDropdown'

import { useToast } from '@sketch/toasts'

import {
  CollapsibleButton,
  ChevronRight,
  ChevronDown,
  NestedProjectsWrapper,
} from './ProjectListItem.styles'

import { useGetProjects } from '../../operations'

import { ReactComponent as PinIcon } from '@sketch/icons/pushpin-17'
import { ReactComponent as MyDraftsIcon } from '@sketch/icons/notes-17'

import { ProjectProps } from './ProjectListItem.types'

import {
  privacyIcon,
  hasProject,
  calculateNestedLevel,
} from './ProjectListItem.utils'

import { isMyDrafts } from '../../utils'
import { MovedToProjectToast } from '../../../storageItems/components'
import { useTransferStorageItem } from '../../../storageItems'
import { ProjectFragment, ShareListItemFragment } from '@sketch/gql-types'

interface UseTransferShareProps {
  workspaceId: string
  newProject: ProjectFragment
}
const useTransferShare = (props: UseTransferShareProps) => {
  const { showToast } = useToast()
  const propsRef = useRef(props)
  propsRef.current = props

  const [moveShareRaw] = useTransferStorageItem({
    onError: errorMessage => {
      showToast(errorMessage, 'negative')
    },
  })

  const moveShare = useCallback(
    (share: ShareListItemFragment, onCompleted?: () => void) => {
      const { newProject, workspaceId } = propsRef.current
      return moveShareRaw({
        onCompleted: () => {
          showToast(
            <MovedToProjectToast
              project={newProject}
              type="Document"
              workspaceId={workspaceId}
            />
          )
          onCompleted?.()
        },
        item: share,
        location: {
          source: {
            workspaceId,
            projectId: share.project?.identifier,
          },
          destination: {
            workspaceId,
            projectId: newProject.identifier,
          },
        },
      })
    },
    [moveShareRaw, showToast]
  )

  return { moveShare }
}

export const ProjectListItem: React.FC<ProjectProps> = props => {
  const {
    link,
    project,
    nestedProjects,
    workspace,
    refToLast,
    setEditedItem,
    forceHighlight = false,
    isProjectList,
  } = props

  const { showModal } = useModalContext()
  const { showToast } = useToast()
  const [isRenaming, setIsRenaming] = useState(false)

  const { projectId } = useParams<RouteParams<'WORKSPACE_PROJECT'>>()

  // An active nested projects is expanded by default
  const [nestedOpen, setNestedOpen] = React.useState(
    hasProject(nestedProjects, projectId)
  )

  const { projectsById } = useGetProjects({
    workspaceId: workspace.identifier,
  })

  const location = useLocation()

  // Let's make sure we show the active project item when the route changes
  useEffect(() => {
    if (hasProject(nestedProjects, projectId)) {
      setNestedOpen(true)

      // scrolls to the expanded project
      refToLast?.current?.scrollIntoView(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // We want to show expanded newly created nested projects
  useOnEvent('nestedProjectCreated', ({ projectId }) => {
    if (hasProject(nestedProjects, projectId)) {
      setNestedOpen(true)
    }
  })

  const { moveShare } = useTransferShare({
    workspaceId: workspace.identifier,
    newProject: project,
  })

  const onDropped = ({ share }: ShareDropPayload) => {
    /**
     * No share nothing to move ¯\_(ツ)_/¯
     */
    if (!share) return

    /**
     * Given that project identifier is the same
     * we friendly remind that the document already exists in that project
     */
    if (
      share.project?.identifier &&
      share.project?.identifier === project.identifier
    ) {
      showToast('Document is already on this project')
      return
    }
    /**
     * If the project shares the same top level project
     * we can move the document without any confirmation
     * as the sharing permissions are the same
     */
    if (
      (project.topLevelProject || project)?.identifier ===
      (share.project?.topLevelProject || share.project)?.identifier
    ) {
      moveShare(share)
      return
    }

    showModal(ConfirmMoveModal, {
      share,
      newProject: project,
      onConfirm: async hideModal => {
        await moveShare(share, hideModal)
      },
    })
  }

  const workspaceId = workspace.identifier

  const toggleEditor = () => setIsRenaming(!isRenaming)
  const onCompleted = () => {
    setEditedItem?.(project.identifier)
  }

  // We should show the pin icon when FF is enabled
  // when the project is pinned and when it's not
  // a private project, as the lock icon should prevail
  // https://www.sketch.com/s/d57ba117-58de-4a49-b217-0720404f0a08/a/oM73ayj
  const showPinIcon =
    !isProjectList &&
    project.pinnedByCurrentUserAt &&
    project.projectPrivacyIcon !== 'INVITED_WORKSPACE_MEMBERS'

  // This function decides which icon needs to be shown for the project, these
  // situations are covered:
  // - The project belongs to drafts, so we show the drafts icon
  // - The project is pinned so we show the pin icon
  // - The project is not pinned so we show a folder icon
  // - The project is private so we show a lock icon
  // - The project is shared with guests so we show a people icon
  // - The project has nested project, so we show chevrons on hover
  const setIcon = () => {
    const nestedProjectIcons = {
      ...(nestedProjects.length && {
        hoverIcon: nestedOpen ? ChevronDown : ChevronRight,
      }),
      ...(nestedProjects.length && {
        hoverIconActive: nestedOpen ? ChevronDown : ChevronRight,
      }),
    }

    if (project.projectType === 'PERSONAL_DRAFTS') {
      return {
        icon: MyDraftsIcon,
        activeIcon: MyDraftsIcon,
        label: 'My Drafts',
        ...nestedProjectIcons,
      }
    }

    return showPinIcon
      ? {
          icon: PinIcon,
          activeIcon: PinIcon,
          label: 'Pin icon',
          ...nestedProjectIcons,
        }
      : {
          icon: privacyIcon(project).component,
          label: privacyIcon(project).label,
          activeIcon: privacyIcon(project, true).component,
          ...nestedProjectIcons,
        }
  }

  const Trigger: React.FC = ({ children }) => (
    <Collapsible.Trigger asChild>
      <CollapsibleButton>{children}</CollapsibleButton>
    </Collapsible.Trigger>
  )
  const icon = setIcon()

  const hasChildren = nestedProjects.length > 0

  const depth = calculateNestedLevel(projectsById, project.identifier)

  const linkItem = isRenaming ? (
    <RenameInlineEditor
      depth={depth}
      project={project}
      workspaceId={workspaceId}
      placeholder={project.name || 'Folder'}
      toggleEditor={toggleEditor}
      onCompleted={onCompleted}
    />
  ) : (
    <DroppableLinkItem
      icon={icon}
      iconLabel={icon.label}
      {...(hasChildren && { iconWrapper: Trigger })}
      to={link}
      text={project.name}
      onDropped={onDropped}
      action={
        !isMyDrafts(project) && (
          <SelectDropdownActionDropdown
            text="Rename & More…"
            content={
              <ProjectDropdown
                workspace={workspace}
                project={project}
                onRename={() => setIsRenaming(!isRenaming)}
              />
            }
          />
        )
      }
      ref={refToLast}
      aria-label={`Link to ${project.name}`}
      forceHighlight={forceHighlight}
    />
  )

  return nestedProjects.length ? (
    <Collapsible.Root open={nestedOpen} onOpenChange={setNestedOpen}>
      {linkItem}
      <Collapsible.Content>
        <NestedProjectsWrapper level={depth}>
          {nestedProjects.map(projectLink => (
            <ProjectListItem
              key={projectLink.project.identifier}
              project={projectLink.project}
              link={projectLink.link}
              nestedProjects={projectLink.nestedProjects}
              workspace={workspace}
              refToLast={refToLast}
              setEditedItem={setEditedItem}
              forceHighlight={forceHighlight}
              isProjectList={isProjectList}
            />
          ))}
        </NestedProjectsWrapper>
      </Collapsible.Content>
    </Collapsible.Root>
  ) : (
    linkItem
  )
}
